import { useRequestCreateTransaction, useRequestPayTransaction } from '@/hooks/httpRequest';
import { Button, DialogFooter } from '@/components/ui';
import { useFormContext, useWatch } from 'react-hook-form';
import { notify } from '@/functions';
import { Loader2, CheckCircle } from 'lucide-react';
import type { TFormData } from '.';
import { broadcastService } from '@/services';
import { TransactionCategoryEnum } from '@/enums';
import type { ICreateTransactionData, ITransactionFormFooterProps } from '@/interfaces';

const FormCreateOrEditTransactionFooter = ({ onSuccess, onCancel }: ITransactionFormFooterProps) => {
	const { createTransaction, isLoading: isLoadingCreate } = useRequestCreateTransaction();
	const { payTransaction, isLoading: isLoadingPay } = useRequestPayTransaction();
	const {
		handleSubmit,
		setValue,
		control,
		formState: { isValid }
	} = useFormContext<TFormData>();
	const readonly = useWatch({ control, name: 'readonly' });
	const _id = useWatch({ control, name: '_id' });
	const isSubmitting = useWatch({ control, name: 'isSubmitting' });
	const category = useWatch({ control, name: 'category' });
	const status = useWatch({ control, name: 'data.status' });

	const isLoading = isLoadingCreate || isSubmitting || isLoadingPay;
	const isEditMode = !!_id;

	const handleOnClickBtnCancel = () => {
		if (onCancel) {
			onCancel();
		} else {
			broadcastService.modal.hide();
		}
	};

	const handleOnClickBtnSave = () =>
		handleSubmit(async data => {
			setValue('isSubmitting', true);
			try {
				// Clean up data to avoid sending empty strings or irrelevant fields
				const sanitizedData = { ...data.data };

				// Remove category-specific fields if they don't apply
				if (data.category !== TransactionCategoryEnum.SALARY) {
					delete sanitizedData.employeeId;
				}
				if (data.category !== TransactionCategoryEnum.INVESTOR) {
					delete sanitizedData.investorId;
				}
				if (data.category !== TransactionCategoryEnum.IN_APP_PURCHASE) {
					delete sanitizedData.paymentRef;
					delete sanitizedData.userId;
				}

				// Remove any remaining empty strings
				(Object.keys(sanitizedData) as Array<keyof typeof sanitizedData>).forEach(key => {
					if (sanitizedData[key] === '') {
						delete sanitizedData[key];
					}
				});

				const submitData: ICreateTransactionData = {
					amountEur: Number(data.amountEur),
					occurredAt: data.occurredAt,
					category: data.category,
					mode: data.mode,
					type: data.type,
					title: data.title,
					data: {
						...sanitizedData,
						description: data.description
					}
				};

				if (isEditMode) {
					// Logic for edit if needed in future
				} else {
					await createTransaction(submitData);
				}

				notify('La transaction a été créée avec succès');
				if (onSuccess) {
					onSuccess();
				} else {
					broadcastService.modal.hide();
				}
			} catch (error) {
				setValue('isSubmitting', false);
				setValue('errorMessage', (error as Error)?.message);
			}
		})();

	const handleOnClickBtnPay = async () => {
		if (!_id) return;
		try {
			await payTransaction(_id as string);
			notify('La transaction a été marquée comme payée');
			if (onSuccess) {
				onSuccess();
			} else {
				broadcastService.modal.hide();
			}
		} catch (error) {
			setValue('errorMessage', (error as Error)?.message);
		}
	};

	return (
		<DialogFooter className="px-2 md:px-2 py-2 md:py-3 border-t bg-muted/10">
			<div className="w-full flex justify-between">
				<Button onClick={handleOnClickBtnCancel} disabled={isLoading} variant="secondary" type="button" size={'sm'}>
					{readonly ? 'Fermer' : 'Annuler'}
				</Button>
				<div className="flex gap-2">
					{isEditMode &&
						category === TransactionCategoryEnum.INVESTOR &&
						(status === 'PENDING' || (status as string)?.toUpperCase() === 'PENDING') && (
							<Button
								onClick={handleOnClickBtnPay}
								disabled={isLoading}
								variant="outline"
								type="button"
								size={'sm'}
								className="text-emerald-600 border-emerald-600/20 hover:bg-emerald-50 hover:text-emerald-700"
							>
								{isLoadingPay ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									<>
										<CheckCircle className="mr-2 h-4 w-4" />
										Marquer comme payé
									</>
								)}
							</Button>
						)}

					{!readonly && (
						<Button
							onClick={handleOnClickBtnSave}
							disabled={!isValid || isLoading}
							type="button"
							size={'sm'}
							className="min-w-[100px]"
						>
							{isLoading ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									{isEditMode ? 'Modification...' : 'Création...'}
								</>
							) : isEditMode ? (
								'Modifier'
							) : (
								'Créer'
							)}
						</Button>
					)}
				</div>
			</div>
		</DialogFooter>
	);
};

export default FormCreateOrEditTransactionFooter;
